/**
 * Enemy forces by sector and reinforcement by day (2026-10-22, Dr. Tatar; R2-19 enemy per brigade, enemy reinforcements).
 *
 * WHAT IT DOES. The enemy of a brigade is still ONE number for the model: the enemy to friendly strength ratio, which the enemy card turns
 * into a power index with the training, equipment and morale of the card. This module only changes WHERE THE RATIO COMES FROM, per brigade
 * and per day, so that nothing is counted twice:
 *   - quality (training, equipment, morale) stays ONLY in the enemy card; the forces below give SIZE only;
 *   - base size of the brigade's enemy: the phase ratio when the planner typed one for the phase, else the enemy forces entered for the
 *     brigade's sector (day 1), else the overall ratio of the card (the card ratio is the DEFAULT, used only where no sector enemy is entered);
 *   - reinforcements (entries from day 2 on) are ADDED to that base on their day; they are not a second multiplier;
 *   - an enemy in a division sector is SHARED between the brigades of that sector by frontage km, never counted in full against each;
 *   - a resistance level typed in the phase wins over everything (the existing rule): the ratio, and so the reinforcement, is then not read.
 *
 * SIZES. ENEMY_UNIT_TABLE: design (war-authorized) strength, rounded to 100 (Dr. Tatar 2026-10-22: rounding a 2 percent changes the casualties by
 * under 2 percent; the manning factor spans 4x). Primary source for the Soviet-model units: US Army FM 100-2-3 (June 1991), Chapter 4,
 * Personnel and Equipment Recapitulation tables, war-authorized strengths of Soviet forces at the end of 1988 (copy supplied by Dr. Tatar);
 * Warsaw Pact armies, the Romanian army of 1989 included, followed the same model. BTG and brigade: secondary (press, think tanks). See
 * docs/research/RESEARCH_ENEMY_FORCE_DESIGN.md. The manning factor turns design strength into fighting strength (evidence 0.15 to 0.7, BTGs in 2022
 * about 0.5); default 1.0 = as designed; a planner input.
 */

export const ENEMY_FORCES_ON = true;

export const ENEMY_UNIT_TABLE = Object.freeze({
  MR_DIVISION:    { label: 'Motor rifle division',                 design: 13300, exact: '13,294+', source: 'FM 100-2-3 (primary)', status: 'SOURCED' },
  TANK_DIVISION:  { label: 'Tank division',                        design: 11600, exact: '11,620+', source: 'FM 100-2-3 (primary)', status: 'SOURCED' },
  MR_BRIGADE:     { label: 'Motor rifle brigade',                  design: 4000,  exact: '3,600 to 4,600', source: 'press and think-tank reports (secondary)', status: 'SOURCED (range)' },
  MR_REGIMENT:    { label: 'Motor rifle regiment',                 design: 2500,  exact: '2,523 (BTR) / 2,424 (BMP)', source: 'FM 100-2-3 (primary)', status: 'SOURCED' },
  TANK_REGIMENT:  { label: 'Tank regiment (in a motor rifle division)', design: 1100, exact: '1,143', source: 'FM 100-2-3 (primary)', status: 'SOURCED' },
  TANK_REGIMENT_TD: { label: 'Tank regiment (in a tank division)', design: 1600,  exact: '1,640', source: 'FM 100-2-3 (primary)', status: 'SOURCED' },
  BTG:            { label: 'Battalion tactical group',             design: 700,   exact: '600 to 800', source: 'Wikipedia, RUSI, Hadtudomany (secondary)', status: 'SOURCED (range)' },
  MR_BATTALION:   { label: 'Motor rifle battalion',                design: 500,   exact: '525 (BTR) / 497 (BMP)', source: 'FM 100-2-3 (primary)', status: 'SOURCED' },
  TANK_BATTALION: { label: 'Tank battalion',                       design: 150,   exact: '135 (tank regiment) / 165 (in a motor rifle regiment)', source: 'FM 100-2-3 (primary)', status: 'SOURCED' }
});

/** Manning factor presets (design strength x factor = fighting strength). Evidence: docs/research/RESEARCH_ENEMY_FORCE_DESIGN.md. */
export const ENEMY_MANNING_PRESETS = Object.freeze({
  AS_DESIGNED: 1.0,
  WAR_FIELDED: 0.7,    // East German division in peacetime 70 percent of wartime
  DEGRADED: 0.5,       // Russian BTGs in 2022 at about half (US official via CNN)
  LOW_CATEGORY: 0.25   // Soviet low-category divisions 15 to 25 percent
});

const num = (v, name) => {
  if (typeof v !== 'number' || Number.isNaN(v)) throw new Error(`enemyForces: ${name} must be a number (got: ${v})`);
  return v;
};

/** Fighting strength of one entry: count x design size x manning (the entry's own, else the default of the card). */
export function enemySizeOfEntry(entry, defaultManning = 1) {
  const u = ENEMY_UNIT_TABLE[entry?.unit];
  if (!u) throw new Error(`enemyForces: unknown enemy unit '${entry?.unit}' (valid: ${Object.keys(ENEMY_UNIT_TABLE).join(', ')})`);
  const count = num(entry.count, 'count');
  if (count < 0) throw new Error(`enemyForces: count must be >= 0 (got: ${count})`);
  const manning = entry.manning === undefined || entry.manning === null || entry.manning === '' ? defaultManning : num(entry.manning, 'manning');
  if (manning <= 0 || manning > 1.5) throw new Error(`enemyForces: manning must be above 0 and at most 1.5 (got: ${manning})`);
  return count * u.design * manning;
}

/**
 * Sectors from the brigades that hold frontage. Naming (Dr. Tatar): 'Division 1 / Sector 2' = the 2nd frontage slice of Division 1, with its
 * km range and brigade. A force without divisions (a brigade, or brigades without a parent) is 'Sector k'.
 * @param {Array<{id:string, name?:string, division_id?:string, division_name?:string, division_number?:number, start_km:number, end_km:number}>} brigades
 */
export function buildSectors(brigades) {
  const withFront = (brigades || []).filter((b) => typeof b.start_km === 'number' && typeof b.end_km === 'number' && b.end_km > b.start_km);
  const byDiv = new Map();
  withFront.forEach((b) => {
    const key = b.division_id || '__none__';
    if (!byDiv.has(key)) byDiv.set(key, []);
    byDiv.get(key).push(b);
  });
  const divKeys = [...byDiv.keys()].sort((a, b) => {
    const na = byDiv.get(a)[0].division_number ?? 99;
    const nb = byDiv.get(b)[0].division_number ?? 99;
    return na - nb;
  });
  const sectors = [];
  let divIndex = 0;
  divKeys.forEach((key) => {
    const slices = [...byDiv.get(key)].sort((a, b) => a.start_km - b.start_km);
    const hasDivision = key !== '__none__';
    if (hasDivision) divIndex += 1;
    const divNumber = byDiv.get(key)[0].division_number ?? divIndex;
    slices.forEach((b, k) => {
      const widthKm = b.end_km - b.start_km;
      sectors.push({
        id: hasDivision ? `D${divNumber}-S${k + 1}` : `S${sectors.length + 1}`,
        name: hasDivision ? `${b.division_name || `Division ${divNumber}`} / Sector ${k + 1}` : `Sector ${sectors.length + 1}`,
        division_id: hasDivision ? key : null,
        start_km: b.start_km,
        end_km: b.end_km,
        width_km: Math.round(widthKm * 10) / 10,
        brigade_id: b.id,
        brigade_name: b.name || b.id
      });
    });
  });
  return sectors;
}

/** Share (0..1) of an entry's enemy that falls on a brigade (frontage start_km..end_km). */
function shareOnBrigade(entry, sectors, brigadeFront, brigadeId) {
  if (entry.sector === 'KM') {
    const from = num(entry.from_km, 'from_km');
    const to = num(entry.to_km, 'to_km');
    if (to <= from) throw new Error(`enemyForces: the km range must have to_km above from_km (got ${from} to ${to})`);
    const f = brigadeFront?.[brigadeId];
    if (!f) return 0;
    const overlap = Math.max(0, Math.min(to, f.end_km) - Math.max(from, f.start_km));
    return overlap / (to - from);
  }
  if (entry.sector === 'ALL') {
    // the whole front: shared by frontage km over every brigade that has frontage
    const f = brigadeFront?.[brigadeId];
    if (!f) return 0;
    const total = Object.values(brigadeFront).reduce((s, x) => s + (x.end_km - x.start_km), 0);
    return total > 0 ? (f.end_km - f.start_km) / total : 0;
  }
  if (typeof entry.sector === 'string' && entry.sector.startsWith('DIV:')) {
    // a whole division: shared between its sectors by frontage km
    const divId = entry.sector.slice(4);
    const mine = sectors.find((s) => s.division_id === divId && s.brigade_id === brigadeId);
    if (!mine) return 0;
    const total = sectors.filter((s) => s.division_id === divId).reduce((t, s) => t + (s.end_km - s.start_km), 0);
    return total > 0 ? (mine.end_km - mine.start_km) / total : 0;
  }
  const sector = sectors.find((s) => s.id === entry.sector);
  if (!sector) return 0;
  if (sector.brigade_id === brigadeId) {
    // a sector is one brigade slice; the enemy of a division is entered on its sectors, or on 'DIV' entries shared below
    return 1;
  }
  return 0;
}

/**
 * Enemy size facing a brigade on a day: base (initial entries, day 1) and reinforcements (entries from day 2 up to the day).
 * Returns { initial, reinforcement, entries } where each number is fighting strength in people.
 */
export function enemyFacingBrigade({ forces, brigadeId, day }) {
  if (!forces || !Array.isArray(forces.entries) || forces.entries.length === 0) return { initial: 0, reinforcement: 0, entries: [], any_initial: false };
  const defaultManning = forces.default_manning ?? 1;
  const sectors = forces.sectors || [];
  const brigadeFront = forces.brigade_frontage || {};
  let initial = 0;
  let reinforcement = 0;
  let anyInitial = false;
  const used = [];
  forces.entries.forEach((e) => {
    const fromDay = e.from_day === undefined || e.from_day === null || e.from_day === '' ? 1 : num(e.from_day, 'from_day');
    if (fromDay < 1) throw new Error(`enemyForces: from_day must be >= 1 (got: ${fromDay})`);
    const share = shareOnBrigade(e, sectors, brigadeFront, brigadeId);
    if (share <= 0) return;
    if (fromDay === 1) anyInitial = true;
    if (fromDay > day) return;
    const size = enemySizeOfEntry(e, defaultManning) * share;
    if (fromDay === 1) initial += size; else reinforcement += size;
    used.push({ id: e.id, unit: e.unit, from_day: fromDay, size: Math.round(size) });
  });
  return { initial, reinforcement, entries: used, any_initial: anyInitial };
}

/**
 * The enemy to friendly ratio of a brigade on a day.
 * @param {Object} p
 * @param {number|undefined} p.phaseRatio  ratio typed in the phase (wins over the sector base)
 * @param {number|undefined} p.cardRatio   overall ratio of the enemy card (default base)
 * @param {Object|null} p.forces           operation_parameters.enemy_forces
 * @param {number} p.friendlyStrength      current strength of the brigade
 * @returns {{ratio:number|undefined, source:string, initial:number, reinforcement:number, entries:Array}}
 *   ratio undefined = nothing from the forces applies (the caller keeps its own ratio).
 */
export function brigadeEnemyRatio({ phaseRatio, cardRatio, forces, brigadeId, day, friendlyStrength }) {
  if (!ENEMY_FORCES_ON) return { ratio: undefined, source: 'OFF', initial: 0, reinforcement: 0, entries: [] };
  const facing = enemyFacingBrigade({ forces, brigadeId, day });
  if (facing.entries.length === 0 && !facing.any_initial) return { ratio: undefined, source: 'NONE', initial: 0, reinforcement: 0, entries: [] };
  if (typeof friendlyStrength !== 'number' || Number.isNaN(friendlyStrength) || friendlyStrength <= 0) {
    throw new Error(`enemyForces: friendly strength must be a number above 0 (got: ${friendlyStrength})`);
  }
  let baseRatio;
  let source;
  if (typeof phaseRatio === 'number' && phaseRatio > 0) { baseRatio = phaseRatio; source = 'PHASE_RATIO'; }
  else if (facing.any_initial) { baseRatio = facing.initial / friendlyStrength; source = 'SECTOR_FORCES'; }
  else if (typeof cardRatio === 'number' && cardRatio > 0) { baseRatio = cardRatio; source = 'CARD_RATIO'; }
  else { baseRatio = 1; source = 'DEFAULT_RATIO'; }
  const add = facing.reinforcement / friendlyStrength;
  return {
    ratio: baseRatio + add,
    source: add > 0 ? `${source}+REINFORCEMENT` : source,
    initial: Math.round(facing.initial),
    reinforcement: Math.round(facing.reinforcement),
    entries: facing.entries
  };
}
